<template>
  <div class="checkout-step-two">
    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">
            {{ $t('pages.checkoutStepTwo.title') }}
          </h3>
          <p class="checkout-step__copy">
            {{ $t('pages.checkoutStepTwo.copy') }}
          </p>
        </div>
        <div class="col-12 col-md-3">
          <small class="checkout-step__copy checkout-step__copy--small">
            {{ $t('generic.priceInfo') }}
          </small>
        </div>
      </div>
    </div>

    <div class="campsite-type-selector">
      <div class="row">
        <template v-for="(campsiteType, index) in campsiteTypes">
          <div
            :key="index"
            v-if="getCampsites(campsiteType[0]).length > 0"
            :class="campsiteTypeClass"
          >
            <div
              class="campsite-type white-box"
              :class="{ active: steps[2].selectedCampsiteType === campsiteType[0] }"
              @click="setCampsiteType(campsiteType)"
            >
              {{ campsiteType[1] }} {{ getAvailabilitiesString(campsiteType[0]) }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="campsite-selector" ref="campsiteSelector">
      <CiAccommodation
        v-for="campsite in getAvailableCampsites"
        v-if="campsite.campsite_type === steps[2].selectedCampsiteType"
        :campsite="campsite"
        :campground="campground"
        :steps="steps"
        :status="status"
        :language="language"
        :active-step="activeStep"
        :currency="currency"
        :attributes="getAttributes(campsite)"
        :is-active="isSelectedCampsite(campsite)"
        :key="campsite.id"
        :nights="numNights"
        @to-step="goToStep"
        @select-campsite="setSelectedCampsite"
      ></CiAccommodation>
    </div>

    <CiProgressNavigation
      class="campground__navigation--inline"
      v-if="selectedCampsite === null"
      :active-step="activeStep"
      :status="status"
      :steps="steps"
    >
    </CiProgressNavigation>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { CiAccommodation, CiProgressNavigation } from '@/components'
import { isElementInViewport } from '@/utils/general'
import { getJsDate } from '@/utils/date'
import { mapGetters, mapActions } from 'vuex'
import { Api } from '@/api/api'

// import iconArrowBlack from '@/assets/icons/arrow-black.svg'
// import iconArrowWhite from '@/assets/icons/arrow-white.svg'

export default {
  name: 'CheckoutStepTwo',

  data() {
    return {
      attributes: [],
      campsiteTypeClass: '',
    }
  },
  components: {
    CiAccommodation,
    CiProgressNavigation,
    // iconArrowBlack,
    // iconArrowWhite,
  },

  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      dates: 'dates',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      selectedSeasons: 'selectedSeasons',
    }),

    ...mapGetters('campground', {
      availableCampsites: 'availableCampsites',
      campground: 'campground',
      campsiteTypes: 'campsiteTypes',
      status: 'status',
    }),

    getAvailableCampsites() {
      // const filtered = this.availableCampsites.filter(campsite => campsite.amount > 0)
      return this.availableCampsites.map(campsite => {
        const basicCampsite = this.campground.campsites.find(cp => cp.id === campsite.id)
        return { ...basicCampsite, ...campsite }
      })
    },
    getRealCampsiteTypesCount() {
      let result = 0
      let campsiteType = null
      for (campsiteType of this.campsiteTypes) {
        let campsites = this.getCampsites(campsiteType[0])
        if (campsites.length) {
          result++
        }
      }
      return result
    },
    numNights() {
      const d = this.dates
      if (!d || !d[1] || d[0] == null || d[1] == null) {
        return 0
      }

      const d1 = DateTime.fromJSDate(getJsDate(d[0]))
      const d2 = DateTime.fromJSDate(getJsDate(d[1]))

      return Math.round(Math.abs(d2.diff(d1, 'days').as('days')))
    },
  },

  async mounted() {
    this.attributes = await Api.getAccommodationAttributes(this.campground.slug)
    let campsiteTypeCount = this.getRealCampsiteTypesCount
    let size = campsiteTypeCount < 4 ? 12 / campsiteTypeCount : 6
    this.campsiteTypeClass = 'col-sm-' + size
  },

  methods: {
    ...mapActions('application', {
      setStepStatus: 'setStepStatus',
    }),

    ...mapActions('booking', {
      setSelectedCampsite: 'setSelectedCampsite',
    }),

    getAttributes(campsite) {
      if (this.attributes.length > 0) {
        const { attributes } = this.attributes.find(attributes => attributes.id === campsite.id)

        return attributes
      }

      return []
    },

    getCampsites(type) {
      return this.getAvailableCampsites.filter(cp => cp.campsite_type === type)
    },

    getAvailabilitiesByCampSite(type) {
      const campsites = this.getCampsites(type)

      return campsites.filter(cp => cp.amount > 0).length
    },

    getAvailabilitiesString(type) {
      const campsites = this.getCampsites(type)
      const amountsAvailable = this.getAvailabilitiesByCampSite(type)

      return `(${amountsAvailable}/${campsites.length})`
    },

    setCampsiteType(campsiteType) {
      // show warning modal if campsite is selected and users switches campsite type
      if (this.selectedCampsite) {
        this.$modal.show('dialog', {
          title: this.$t('modals.CiModalCampsiteTypeWarning.title'),
          content: this.$t('modals.CiModalCampsiteTypeWarning.content'),
          proceedEvent: /* istanbul ignore next */ () => this.onChangeCampsiteType(campsiteType),
          selectedCampsite: this.selectedCampsite,
        })
      } else {
        // set selected campsite type into step status
        this.setStepStatus({ number: 2, key: 'selectedCampsiteType', value: campsiteType[0] })

        // scroll campsites into viewport if they aren't
        this.$nextTick(() => {
          /* istanbul ignore else */
          if (!isElementInViewport(this.$refs.campsiteSelector)) {
            this.$refs.campsiteSelector.scrollIntoView({ block: 'start', behavior: 'smooth' })
          }
        })
      }
    },

    // if user agrees to change campsite type inside the warning modal
    onChangeCampsiteType(campsiteType) {
      this.setSelectedCampsite(null)
      this.setStepStatus({ number: 2, key: 'selectedCampsiteType', value: campsiteType[0] })
    },

    isSelectedCampsite(campsite) {
      if (!campsite) return
      return (
        campsite.campsite_type === this.steps[2].selectedCampsiteType &&
        (this.selectedCampsite && campsite.id === this.selectedCampsite.id)
      )
    },

    getWeekdaysString(days) {
      let names = []
      let idx = 0
      let hasSunday = false
      for (let day = 4; day <= 10; day++) {
        if (days.indexOf(idx) !== -1) {
          if (idx === 0) {
            hasSunday = true
          } else {
            names.push(
              new Date(1970, 0, day).toLocaleString(this.language.code, { weekday: 'long' })
            )
          }
        }
        idx++
      }
      if (hasSunday) {
        names.push(new Date(1970, 0, 4).toLocaleString(this.language.code, { weekday: 'long' }))
      }
      return names.join(', ')
    },

    goToStep(stepData) {
      this.setStepStatus(stepData)
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: stepData.number },
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.checkout-step-two {
  padding: 0;

  @include media-breakpoint-up(md) {
    padding: 0 0 60px;
  }

  @include media-breakpoint-up(lg) {
    padding: 0;
  }
}

.checkout-step-two__header {
  margin: 0 0 30px;
}

.checkout-step-two__headline {
  @include font-format(1.375rem, 1.625em);
}

.campsite-type-selector {
  margin-bottom: 10px;

  @include media-breakpoint-up(xl) {
    margin-bottom: 30px;
  }
}

.campsite-type {
  @include font('gotham_mediumregular');

  align-items: center;
  display: flex;
  height: 60px;
  justify-content: center;
  margin: 0 0 10px;
  cursor: pointer;
  transition: background-color 0.4s ease;

  .no-touch &:hover,
  &.active {
    color: white;
    background: var(--primary-color);
  }
}
</style>
